/**
 * Generic wrapper for section blocks with configurable in/out layout animations.
 * Android: entering uses opaque transform-only motion so studio gradients do not flash through card edges.
 */
import React, { useMemo } from 'react';
import { Platform, StyleProp, StyleSheet, ViewStyle, type ViewProps } from 'react-native';
import Animated, {
  FadeIn,
  FadeInDown,
  FadeInUp,
  FadeOut,
  FadeOutUp,
  FadeOutDown,
  SlideInLeft,
  SlideInRight,
  SlideInDown,
  SlideInUp,
  SlideOutLeft,
  SlideOutRight,
  SlideOutDown,
  SlideOutUp,
  ZoomIn,
  ZoomOut,
  type AnimatedStyle,
  type EntryOrExitLayoutType,
} from 'react-native-reanimated';
import { appTheme } from '../../shared/theme/appTheme';
import { buildAndroidOpaqueSectionEntering } from './animatedSectionEnter.android';

const DEFAULT_STAGGER_MS = 32;
const DEFAULT_DURATION = appTheme.animations.duration.fast + 30;
const DEFAULT_SPRING = { damping: 42, stiffness: 280 };

export type AnimatedSectionVariant =
  | 'none'
  | 'fade'
  | 'fadeDown'
  | 'fadeUp'
  | 'slideHorizontal'
  | 'slideVerticalDown'
  | 'slideVerticalUp'
  | 'scale';

/** Navigation direction for horizontal slide: forward = from right, back = from left. */
export type NavigationDirection = 'forward' | 'back';

export interface AnimatedSectionProps {
  children: React.ReactNode;
  index?: number;
  use_fade_only?: boolean;
  variant?: AnimatedSectionVariant;
  navigationDirection?: NavigationDirection;
  duration?: number;
  delay?: number;
  staggerMs?: number;
  useSpring?: boolean;
  springConfig?: { damping?: number; stiffness?: number };
  enableExiting?: boolean;
  entering?: EntryOrExitLayoutType;
  exiting?: EntryOrExitLayoutType;
  style?: StyleProp<ViewStyle>;
  animatedStyle?: AnimatedStyle<ViewStyle>;
  pointerEvents?: ViewProps['pointerEvents'];
  onLayout?: ViewProps['onLayout'];
  collapsable?: ViewProps['collapsable'];
}

type BuilderLike = {
  delay: (ms: number) => BuilderLike;
  duration: (ms: number) => BuilderLike;
  springify: () => BuilderLike;
  damping: (n: number) => BuilderLike;
  stiffness: (n: number) => BuilderLike;
};

function applyDelayDurationSpring(
  builder: BuilderLike,
  delay_ms: number,
  duration_ms: number,
  use_spring: boolean,
  spring_config: { damping: number; stiffness: number },
): EntryOrExitLayoutType {
  let out: BuilderLike = builder.delay(delay_ms).duration(duration_ms);
  if (use_spring) {
    out = out.springify().damping(spring_config.damping).stiffness(spring_config.stiffness);
  }
  return out as unknown as EntryOrExitLayoutType;
}

export const AnimatedSection = React.forwardRef<Animated.View, AnimatedSectionProps>(function AnimatedSection(
  {
    children,
    index = 0,
    use_fade_only,
    variant: variantProp,
    navigationDirection,
    duration = DEFAULT_DURATION,
    delay: extra_delay = 0,
    staggerMs = DEFAULT_STAGGER_MS,
    useSpring = false,
    springConfig,
    enableExiting = false,
    entering: entering_override,
    exiting: exiting_override,
    style,
    animatedStyle,
    pointerEvents,
    onLayout,
    collapsable,
  }: AnimatedSectionProps,
  ref: React.ForwardedRef<Animated.View>,
): React.ReactElement {
  const variant = use_fade_only ? 'fade' : (variantProp ?? 'fadeDown');

  const spring = useMemo(() => ({ ...DEFAULT_SPRING, ...springConfig }), [springConfig]);

  const { entering, exiting } = useMemo((): {
    entering: EntryOrExitLayoutType | undefined;
    exiting: EntryOrExitLayoutType | undefined;
  } => {
    if (variant === 'none') {
      return { entering: undefined, exiting: undefined };
    }
    const total_delay = index * staggerMs + extra_delay;

    if (entering_override && exiting_override) {
      return { entering: entering_override, exiting: exiting_override };
    }

    if (entering_override) {
      return {
        entering: entering_override,
        exiting: enableExiting ? exiting_override : undefined,
      };
    }

    if (Platform.OS === 'android') {
      return {
        entering: buildAndroidOpaqueSectionEntering(variant, total_delay, duration, useSpring, spring, navigationDirection),
        exiting: enableExiting ? undefined : undefined,
      };
    }

    const apply = (E: BuilderLike, X?: BuilderLike) => {
      const ent = applyDelayDurationSpring(E, total_delay, duration, useSpring, spring);
      const ext = enableExiting && X ? applyDelayDurationSpring(X, 0, duration, useSpring, spring) : undefined;
      return { entering: ent, exiting: ext };
    };

    let result: { entering: EntryOrExitLayoutType; exiting: EntryOrExitLayoutType | undefined };
    switch (variant) {
      case 'fade':
        result = apply(FadeIn as BuilderLike, FadeOut as BuilderLike);
        break;
      case 'fadeDown':
        result = apply(FadeInDown as BuilderLike, FadeOutUp as BuilderLike);
        break;
      case 'fadeUp':
        result = apply(FadeInUp as BuilderLike, FadeOutDown as BuilderLike);
        break;
      case 'slideHorizontal': {
        const forward = navigationDirection !== 'back';
        result = forward
          ? apply(SlideInRight as BuilderLike, SlideOutLeft as BuilderLike)
          : apply(SlideInLeft as BuilderLike, SlideOutRight as BuilderLike);
        break;
      }
      case 'slideVerticalDown':
        result = apply(SlideInDown as BuilderLike, SlideOutUp as BuilderLike);
        break;
      case 'slideVerticalUp':
        result = apply(SlideInUp as BuilderLike, SlideOutDown as BuilderLike);
        break;
      case 'scale':
        result = apply(ZoomIn as BuilderLike, ZoomOut as BuilderLike);
        break;
      default:
        result = apply(FadeInDown as BuilderLike, FadeOutUp as BuilderLike);
    }
    if (exiting_override && enableExiting) {
      result = { ...result, exiting: exiting_override };
    }
    return result;
  }, [
    index,
    staggerMs,
    extra_delay,
    variant,
    navigationDirection,
    duration,
    useSpring,
    spring,
    enableExiting,
    entering_override,
    exiting_override,
  ]);

  return (
    <Animated.View
      ref={ref}
      entering={entering}
      exiting={exiting}
      style={[style, animatedStyle, Platform.OS === 'android' && styles.android_host]}
      pointerEvents={pointerEvents}
      onLayout={onLayout}
      collapsable={Platform.OS === 'android' ? false : collapsable}>
      {children}
    </Animated.View>
  );
});

const styles = StyleSheet.create({
  android_host: {
    backfaceVisibility: 'hidden',
  },
});
